import React, { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Science,
  CheckCircle,
  Error,
  Warning,
  Refresh
} from '@mui/icons-material';
import { validateSection } from '../../services/universalSectionValidation.service';

/**
 * Section Validation Indicator Component
 * 
 * A compact component that shows validation status for a section
 * and allows manual validation triggering.
 */

const SectionValidationIndicator = ({
  sectionTitle,
  sectionContent,
  sectionPrompt,
  context = '',
  onValidationComplete = () => {},
  compact = true,
  initialValidationResult = null // New prop to pass validation status from parent
}) => {
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(initialValidationResult);
  const [showDetails, setShowDetails] = useState(false);

  // Show for all sections (validation is now universal)

  // Update validation result when prop changes
  useEffect(() => {
    console.log(`🔍 SectionValidationIndicator for ${sectionTitle} - initialValidationResult:`, initialValidationResult);
    if (initialValidationResult !== null) {
      setValidationResult(initialValidationResult);
      console.log(`✅ SectionValidationIndicator for ${sectionTitle} - validation result updated:`, initialValidationResult);
    }
  }, [initialValidationResult, sectionTitle]);

  const handleValidate = async () => {
    if (!sectionContent || !sectionPrompt) {
      alert('Section content and prompt are required for validation');
      return;
    }

    setIsValidating(true);
    try {
      console.log(`🔍 Validating ${sectionTitle} content...`);
      const result = await validateSection(sectionTitle, sectionContent, sectionPrompt, context);
      setValidationResult(result);
      onValidationComplete(result);
      console.log(`✅ ${sectionTitle} validation completed:`, result);
    } catch (error) {
      console.error(`❌ ${sectionTitle} validation failed:`, error);
      setValidationResult({
        isValid: false,
        overallScore: 0,
        errors: [{
          subsection: 'system',
          errorType: 'validation_error',
          description: error.message,
          severity: 'critical',
          suggestion: 'Check validation service'
        }]
      });
    } finally {
      setIsValidating(false);
    }
  };

  const getStatusIcon = () => {
    console.log(`🎯 getStatusIcon for ${sectionTitle}:`, {
      isValidating,
      validationResult,
      hasValidationResult: !!validationResult,
      isValid: validationResult?.isValid,
      overallScore: validationResult?.overallScore
    });
    
    if (isValidating) {
      return <CircularProgress size={16} />;
    }
    if (!validationResult) {
      return <Science color="action" />;
    }
    if (validationResult.isValid) {
      return <CheckCircle color="success" />;
    }
    if (validationResult.overallScore >= 70) {
      return <Warning color="warning" />;
    }
    return <Error color="error" />;
  };

  const getStatusColor = () => {
    if (isValidating) return 'default';
    if (!validationResult) return 'default';
    if (validationResult.isValid) return 'success';
    if (validationResult.overallScore >= 70) return 'warning';
    return 'error';
  };

  const getStatusText = () => {
    console.log(`🎯 getStatusText for ${sectionTitle}:`, {
      isValidating,
      validationResult,
      hasValidationResult: !!validationResult,
      isValid: validationResult?.isValid,
      overallScore: validationResult?.overallScore
    });
    
    if (isValidating) return 'Validating...';
    if (!validationResult) return 'Not Validated';
    if (validationResult.isValid) return 'Valid';
    if (validationResult.overallScore >= 70) return 'Needs Improvement';
    return 'Invalid';
  };

  if (compact) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title={`Validation Status: ${getStatusText()}`}>
          <Chip
            icon={getStatusIcon()}
            label={getStatusText()}
            color={getStatusColor()}
            size="small"
            onClick={handleValidate}
            disabled={isValidating || !sectionContent || !sectionPrompt}
          />
        </Tooltip>
        
        {validationResult && (
          <Tooltip title="View validation details">
            <IconButton 
              size="small" 
              onClick={() => setShowDetails(true)}
              disabled={isValidating}
            >
              <Science fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Chip
          icon={getStatusIcon()}
          label={getStatusText()}
          color={getStatusColor()}
          onClick={handleValidate}
          disabled={isValidating || !sectionContent || !sectionPrompt}
        />
        
        <Button
          variant="outlined"
          size="small"
          startIcon={<Refresh />}
          onClick={handleValidate}
          disabled={isValidating || !sectionContent || !sectionPrompt}
        >
          {isValidating ? 'Validating...' : 'Validate Section'}
        </Button>
      </Box>

      {/* Validation Details Dialog */}
      <Dialog 
        open={showDetails} 
        onClose={() => setShowDetails(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {sectionTitle} Validation Details
        </DialogTitle>
        <DialogContent>
          {validationResult ? (
            <Box>
              <Alert 
                severity={validationResult.isValid ? 'success' : 'error'}
                sx={{ mb: 2 }}
              >
                <Typography variant="h6">
                  Validation Result: {validationResult.isValid ? 'PASSED' : 'FAILED'}
                </Typography>
                <Typography variant="body2">
                  Overall Score: {validationResult.overallScore}/100
                </Typography>
              </Alert>

              {validationResult.errors && validationResult.errors.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Issues Found ({validationResult.errors.length})
                  </Typography>
                  {validationResult.errors.map((error, index) => (
                    <Alert key={index} severity={error.severity} sx={{ mb: 1 }}>
                      <Typography variant="subtitle2">
                        {error.subsection}: {error.description}
                      </Typography>
                      <Typography variant="body2">
                        Suggestion: {error.suggestion}
                      </Typography>
                    </Alert>
                  ))}
                </Box>
              )}

              {validationResult.strengths && validationResult.strengths.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Strengths
                  </Typography>
                  {validationResult.strengths.map((strength, index) => (
                    <Alert key={index} severity="success" sx={{ mb: 1 }}>
                      {strength}
                    </Alert>
                  ))}
                </Box>
              )}

              {validationResult.recommendations && validationResult.recommendations.length > 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Recommendations
                  </Typography>
                  {validationResult.recommendations.map((recommendation, index) => (
                    <Alert key={index} severity="info" sx={{ mb: 1 }}>
                      {recommendation}
                    </Alert>
                  ))}
                </Box>
              )}
            </Box>
          ) : (
            <Typography>
              No validation results available. Click "Validate Section" to check this section.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetails(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SectionValidationIndicator;
